import React, { useEffect, useState, useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import ApiClient from '../../utils/ApiClient';
import { getUserEmail } from '../../auth';
import toast from 'react-hot-toast';

function loadDymoScript() {
    return new Promise((resolve, reject) => {
        if (document.getElementById('dymo-script')) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.id = 'dymo-script';
        script.src = '/dynmo/dymo.connect.framework.js'; // Ensure this path is correct
        script.onload = resolve;
        script.onerror = () => reject(new Error('Failed to load the DYMO script'));
        document.body.appendChild(script);
    });
}

function ReceiveUnitsModal({ open, onClose, onSaved, data, dispositionData, trackingData, trackingId, generatedCases = [], onCaseCreated }) {
    const [qrCodeUrl, setQrCodeUrl] = useState(null);
    const [itemCode, setItemCode] = useState('');
    const [quantities, setQuantities] = useState({});
    const [upcs, setUpcs] = useState({}); // Store UPC for each disposition
    const [fnskus, setFnskus] = useState({}); // Store FNSKU for each disposition
    const [loadingFnskus, setLoadingFnskus] = useState(false); // Track FNSKU loading state
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false); // guards against double-submit
    const [lpnInput, setLpnInput] = useState('');
    const [lpnData, setLpnData] = useState(null);
    const [lpnError, setLpnError] = useState(false);
    const [lpnLoading, setLpnLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('record'); // 'record' or 'case'
    const [currentCaseId, setCurrentCaseId] = useState(null);
    const [currentCaseQuantity, setCurrentCaseQuantity] = useState(null);
    const [currentCaseDisposition, setCurrentCaseDisposition] = useState(null);
    const [showPrintQtyModal, setShowPrintQtyModal] = useState(false);
    const [printQty, setPrintQty] = useState('1');
    const [labelSize, setLabelSize] = useState('30336'); // Default to 1" x 2-1/8"
    const [pendingPrintData, setPendingPrintData] = useState(null);
    const [selectedCaseIndex, setSelectedCaseIndex] = useState(null); // Track which case is selected for viewing
    const [showQrCodeForDisposition, setShowQrCodeForDisposition] = useState(null); // Track which disposition has QR visible
    const [showEmailModal, setShowEmailModal] = useState(false); // Track email modal visibility
    const [emailInput, setEmailInput] = useState(''); // Store email input
    const [selectedCaseForEmail, setSelectedCaseForEmail] = useState(null); // Track which case to email
    const [emailSending, setEmailSending] = useState(false); // Track email sending state
    const [casesLoadedFromDb, setCasesLoadedFromDb] = useState(false); // Track if cases were loaded from DB
    const [showListingModal, setShowListingModal] = useState(false); // Track listing creation modal
    const [existingListings, setExistingListings] = useState(null); // Store existing listings check result
    const [creatingListings, setCreatingListings] = useState(false); // Track listing creation state
    const [pollingForFnskus, setPollingForFnskus] = useState(false); // Track FNSKU polling state

    // Auto-switch to case tab when cases exist
    useEffect(() => {
        if (generatedCases && generatedCases.length > 0 && casesLoadedFromDb) {
            setActiveTab('case');
        }
    }, [generatedCases, casesLoadedFromDb]);

    // Calculate total quantity entered across all dispositions
    const totalEntered = Object.values(quantities).reduce((sum, qty) => {
        const parsed = parseInt(qty) || 0;
        return sum + parsed;
    }, 0);

    // Expected quantity from the row data
    const expectedQuantity = parseInt(data?.shipped_quantity) || 0;
    // Show the Product-UPC capture row when a DIFFERENT item than expected was received.
    // Keyed by disposition id (stable) — the old `.includes('different unit received')`
    // name match silently matched nothing after the dispositions were reworded to
    // "Different Item Than Expected - {Adjust Inventory(10), Create Case(8)}".
    const UPC_DISPOSITION_IDS = new Set([8, 10]);
    const dispositionRequiresUpc = (d) =>
        UPC_DISPOSITION_IDS.has(d?.id) ||
        (d?.disposition || '').toLowerCase().includes('different item than expected');

    useEffect(() => {
        if (open) {
            // Reset states when modal opens
            setCasesLoadedFromDb(false);
            setActiveTab('record'); // Start on record tab, will auto-switch if cases exist

            loadDymoScript()
                .then(() => {
                    if (window.dymo?.label?.framework) {
                        console.log('DYMO framework loaded');
                        return window.dymo.label.framework.init();
                    } else {
                        throw new Error('DYMO framework not found');
                    }
                })
                .catch((error) => {
                    console.error('Error initializing DYMO framework:', error);
                });

            // Fetch item_code from calibrate_price_lists using asin1
            if (data?.asin1) {
                fetchItemCode(data.asin1);
            }

            // Load existing cases for this product
            if (data?.removal_shipment) {
                loadExistingCases(data.removal_shipment);
            }
        }
    }, [open, data]);

    // Initialize quantities from dispositionData
    useEffect(() => {
        if (dispositionData && dispositionData.length > 0) {
            const initialQuantities = {};
            dispositionData.forEach(item => {
                // Convert to string to work with controlled input
                initialQuantities[item.id] = item.dispositioned ? String(item.dispositioned) : '';
            });
            setQuantities(initialQuantities);
        }
    }, [dispositionData]);

    // Fetch FNSKUs for all dispositions
    useEffect(() => {
        const fetchFnskus = async () => {
            if (!dispositionData || !data?.sku) return;

            setLoadingFnskus(true);
            const fnskuMap = {};

            for (const item of dispositionData) {
                if (item.amazon_id) {
                    try {
                        const response = await ApiClient.post('/api/getfnskubycondition', {
                            sku: data.sku,
                            conditionId: item.amazon_id
                        });
                        fnskuMap[item.id] = response.data?.fnsku || null; // null if not found
                    } catch (error) {
                        console.error(`Error fetching FNSKU for disposition ${item.id}:`, error);
                        fnskuMap[item.id] = null; // No FNSKU available
                    }
                } else {
                    fnskuMap[item.id] = null; // No FNSKU for items without amazon_id
                }
            }

            setFnskus(fnskuMap);
            setLoadingFnskus(false);
        };

        fetchFnskus();
    }, [dispositionData, data]);

    const fetchItemCode = async (asin) => {
        try {
            const response = await ApiClient.get(`/api/getitemcodebyasin?asin=${asin}`);
            setItemCode(response.data?.item_code || '');
        } catch (error) {
            console.error('Error fetching item code:', error);
        }
    };

    const loadExistingCases = async (removalShipment) => {
        try {
            const response = await ApiClient.get(`/api/getcasesbyremovalshipment?removalShipment=${encodeURIComponent(removalShipment)}`);

            if (response.data) {
                const existingCasesFromDB = response.data;

                // Convert database cases to the format expected by generatedCases
                const formattedCases = existingCasesFromDB.map(dbCase => ({
                    caseId: dbCase.qr_case_id,  // Backend returns qr_case_id
                    quantity: dbCase.quantity,
                    disposition: dbCase.disposition,
                    qrCodeUrl: `${process.env.REACT_APP_API_URL}/case-review?caseId=${dbCase.qr_case_id}`,
                    createdAt: dbCase.created_at,  // Backend returns created_at
                    itemName: data?.item_name || '',
                    dispositionId: dbCase.disposition_id  // Backend returns disposition_id
                }));

                // Merge with any existing generated cases (avoid duplicates)
                const mergedCases = [...formattedCases];
                generatedCases.forEach(genCase => {
                    if (!mergedCases.find(c => c.caseId === genCase.caseId)) {
                        mergedCases.push(genCase);
                    }
                });

                // Update parent with merged cases
                if (onCaseCreated && formattedCases.length > 0) {
                    formattedCases.forEach(caseData => {
                        // Only add if not already in generatedCases
                        if (!generatedCases.find(c => c.caseId === caseData.caseId)) {
                            onCaseCreated(caseData);
                        }
                    });
                    // Mark that cases were loaded from DB to trigger auto-switch
                    setCasesLoadedFromDb(true);
                }
            }
        } catch (error) {
            console.error('Error loading existing cases:', error);
        }
    };

    const handleLpnLookup = async (lpn) => {
        if (!lpn || lpn.trim().length === 0) {
            setLpnData(null);
            setLpnError(false);
            return;
        }

        setLpnLoading(true);
        setLpnError(false);

        try {
            const response = await ApiClient.post('/api/getdispositionbylpn', { lpn: lpn.trim() });
            setLpnData(response.data);
            setLpnError(false);
        } catch (error) {
            console.error('Error fetching LPN data:', error);
            setLpnData(null);
            setLpnError(true);
        } finally {
            setLpnLoading(false);
        }
    };

    const handleLpnInputChange = (e) => {
        const value = e.target.value;
        setLpnInput(value);

        // Auto-lookup when user stops typing (debounced)
        const timeoutId = setTimeout(() => {
            handleLpnLookup(value);
        }, 500);

        return () => clearTimeout(timeoutId);
    };

    // Helper function to escape XML special characters
    const escapeXml = (str) => {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    };

    const handlePrintCaseLabel = async (caseId, quantity, disposition) => {
        // Escape XML special characters
        const safeCaseId = escapeXml(caseId);
        const safeDisposition = escapeXml(disposition);

        try {
            // Check if DYMO framework is loaded
            if (!window.dymo || !window.dymo.label || !window.dymo.label.framework) {
                throw new Error('DYMO framework not loaded. Please ensure DYMO Connect software is installed and running.');
            }

            // Get printers
            const printers = window.dymo.label.framework.getPrinters();

            if (!printers || printers.length === 0) {
                throw new Error('No DYMO printers found. Please ensure a DYMO printer is connected and the DYMO Connect software is running.');
            }

            // Find first connected printer (isConnected can be boolean or string)
            const connectedPrinter = printers.find(p => p.isConnected === true || p.isConnected === 'True');
            const printer = connectedPrinter || printers[0];
            const printerName = printer.name;
            console.log('Using printer:', printerName, '(connected:', printer.isConnected, ')');

            // Create label XML for case label (30336 Multipurpose Label - 1" x 2-1/8")
            const labelXml = `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips">
    <PaperOrientation>Landscape</PaperOrientation>
    <Id>Multipurpose</Id>
    <IsOutlined>false</IsOutlined>
    <PaperName>30336 1 in x 2-1/8 in</PaperName>
    <Height>1440</Height>
    <Width>3060</Width>
    <DrawCommands>
        <RoundRectangle X="0" Y="0" Width="1440" Height="3060" Rx="270" Ry="270" />
    </DrawCommands>
    <ObjectInfo>
        <TextObject>
            <Name>CaseId</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeCaseId}</String>
                    <Attributes>
                        <Font Family="Arial" Size="18" Bold="True" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="100" Y="40" Width="2860" Height="400" />
    </ObjectInfo>
    <ObjectInfo>
        <TextObject>
            <Name>Disposition</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeDisposition}</String>
                    <Attributes>
                        <Font Family="Arial" Size="7" Bold="False" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="100" Y="480" Width="2860" Height="240" />
    </ObjectInfo>
    <ObjectInfo>
        <TextObject>
            <Name>Quantity</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>Qty: ${quantity}</String>
                    <Attributes>
                        <Font Family="Arial" Size="12" Bold="True" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="100" Y="760" Width="2860" Height="280" />
    </ObjectInfo>
</DieCutLabel>`;

            // Create label object
            const label = window.dymo.label.framework.openLabelXml(labelXml);

            // Print the label
            label.print(printerName);

            console.log('Case label sent to printer successfully');
        } catch (error) {
            console.error('Error printing case label:', error);
            // Don't show alert here to avoid interrupting the QR code flow
        }
    };

    const handleGenerateQrCode = async (item) => {
        try {
            const token = localStorage.getItem('jwt'); // Retrieve the JWT token
            console.log(item)
            if (!token) {
                throw new Error('User is not authenticated. Please log in again.');
            }

            // Get the quantity from state
            const quantity = quantities[item.id] || 0;

            const response = await ApiClient.post('/api/qrcreatecase', {
                dispositionId: item.id,
                quantity: quantity,
                trackingNumber: trackingData?.tracking_id || data?.tracking_id || '',
                data
            });
            const responseData = response.data;

            // Check if the response is empty or invalid
            if (!responseData || responseData.length === 0 || !responseData[0]?.caseId) {
                throw new Error('Failed to create case ID. Please check the disposition ID and try again.');
            }

            const caseId = responseData[0].caseId;

            const url = new URL(`https://calibrate.io/api/returns/createcase/uploadphotos`);
            url.searchParams.append('QRToken', token);
            url.searchParams.append('caseId', caseId);

            //1ZA8337B0304535126
            //add all parameters to the url of item object
            // Object.keys(data).forEach((key) => {
            //     url.searchParams.append(key, data);
            // });

            // Store case info for reprint functionality
            const dispositionText = item.disposition.replace(/ - Create Case$/i, '');
            const caseData = {
                caseId,
                quantity,
                disposition: dispositionText,
                qrCodeUrl: url.toString(),
                createdAt: new Date().toISOString(),
                itemName: data?.item_name || '',
                dispositionId: item.id
            };

            // Set as current viewing case
            setQrCodeUrl(url.toString());
            setCurrentCaseId(caseId);
            setCurrentCaseQuantity(quantity);
            setCurrentCaseDisposition(dispositionText);
            setShowQrCodeForDisposition(item.id); // Show QR code for this disposition

            // Notify parent component to store the case
            if (onCaseCreated) {
                onCaseCreated(caseData);
            }

            // Print case label after generating QR code
            await handlePrintCaseLabel(caseId, quantity, dispositionText);
        } catch (error) {
            console.error('Error generating QR code:', error);
            toast.error(error.message || 'Failed to generate QR code. Please try again.');
        }
    };

    const handleToggleQrCode = (dispositionId) => {
        // Find the case for this disposition
        const existingCase = generatedCases.find(c => c.dispositionId === dispositionId);

        if (!existingCase) return;

        // Toggle: if currently showing this disposition's QR, hide it; otherwise show it
        if (showQrCodeForDisposition === dispositionId) {
            setShowQrCodeForDisposition(null);
            setQrCodeUrl(null);
            setCurrentCaseId(null);
            setCurrentCaseQuantity(null);
            setCurrentCaseDisposition(null);
        } else {
            setQrCodeUrl(existingCase.qrCodeUrl);
            setCurrentCaseId(existingCase.caseId);
            setCurrentCaseQuantity(existingCase.quantity);
            setCurrentCaseDisposition(existingCase.disposition);
            setShowQrCodeForDisposition(dispositionId);
        }
    };

    // Helper function to check if a case exists for a disposition
    const getCaseForDisposition = (dispositionId) => {
        return generatedCases.find(c => c.dispositionId === dispositionId);
    };

    const handleSendEmailClick = (caseData) => {
        setSelectedCaseForEmail(caseData);
        // Pre-populate with user's email from JWT token
        const userEmail = getUserEmail();
        setEmailInput(userEmail || '');
        setShowEmailModal(true);
    };

    const handleSendEmail = async () => {
        if (!emailInput || !selectedCaseForEmail) return;

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailInput)) {
            toast.error('Please enter a valid email address');
            return;
        }

        setEmailSending(true);

        try {
            const response = await ApiClient.post('/api/sendqrcodelink', {
                toEmail: emailInput,
                caseId: selectedCaseForEmail.caseId,
                qrCodeUrl: selectedCaseForEmail.qrCodeUrl,
                itemName: selectedCaseForEmail.itemName,
                disposition: selectedCaseForEmail.disposition,
                quantity: selectedCaseForEmail.quantity,
                removalShipment: data?.removal_shipment
            });

            // ApiClient (axios) will only reach here if successful (2xx status)
            toast.success('Email sent successfully!');
            setShowEmailModal(false);
            setEmailInput('');
            setSelectedCaseForEmail(null);
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error('Failed to send email. Please try again.');
        } finally {
            setEmailSending(false);
        }
    };

    // Check which listings already exist for this ASIN
    const handleCheckListings = async () => {
        if (!data?.asin1) {
            toast.error('No ASIN available for this item');
            return;
        }

        try {
            const response = await ApiClient.post('/api/listings/check-existing', { asin: data.asin1 });
            setExistingListings(response.data);
            setShowListingModal(true);
        } catch (error) {
            console.error('Error checking listings:', error);
            toast.error('Failed to check existing listings: ' + error.message);
        }
    };

    // Create all missing listings for this ASIN
    const handleCreateListings = async () => {
        if (!data?.asin1) {
            toast.error('No ASIN available for this item');
            return;
        }

        setCreatingListings(true);
        try {
            const response = await ApiClient.post('/api/listings/create-all-conditions', {
                asin: data.asin1,
                sku: data.sku
            });

            const result = response.data;

            if (result.success) {
                const createdCount = (result.created?.fba?.length || 0) + (result.created?.mf?.length || 0);
                const skippedCount = result.skipped?.length || 0;
                const failedCount = result.failed?.length || 0;

                setShowListingModal(false);

                if (createdCount > 0) {
                    toast.success(`Created ${createdCount} listings!\n\nSkipped: ${skippedCount} (already exist)\nFailed: ${failedCount}\n\nFNSKUs will be available shortly. Polling for updates...`);
                    // Start polling for FNSKUs
                    pollForFnskus(data.asin1);
                } else if (skippedCount > 0) {
                    toast.error(`All listings already exist (${skippedCount} skipped).`);
                } else {
                    toast.error('No listings were created.');
                }
            } else {
                throw new Error(result.error || result.message || 'Failed to create listings');
            }
        } catch (error) {
            console.error('Error creating listings:', error);
            toast.error('Failed to create listings: ' + error.message);
        } finally {
            setCreatingListings(false);
        }
    };

    // Poll for FNSKUs after listing creation
    const pollForFnskus = async (asin) => {
        const maxAttempts = 24; // 2 minutes at 5-second intervals
        let attempts = 0;

        setPollingForFnskus(true);

        const poll = async () => {
            attempts++;
            try {
                const response = await ApiClient.post('/api/listings/get-fnskus-by-asin', { asin });

                if (response.data) {
                    const result = response.data;

                    // Update fnskus state with any new FNSKUs found
                    const newFnskus = {};
                    let foundNew = false;

                    result.fnskus?.forEach(item => {
                        if (item.fnsku) {
                            // Map conditionId to disposition item.id
                            const disposition = dispositionData?.find(d => d.amazon_id === item.conditionId);
                            if (disposition && !fnskus[disposition.id]) {
                                newFnskus[disposition.id] = item.fnsku;
                                foundNew = true;
                            }
                        }
                    });

                    if (foundNew) {
                        setFnskus(prev => ({ ...prev, ...newFnskus }));
                    }

                    // Check if all conditions with amazon_id have FNSKUs
                    const conditionsWithAmazonId = dispositionData?.filter(d => d.amazon_id !== null && d.display_order <= 7) || [];
                    const allFound = conditionsWithAmazonId.every(d => fnskus[d.id] || newFnskus[d.id]);

                    if (allFound) {
                        console.log('All FNSKUs found, stopping poll');
                        setPollingForFnskus(false);
                        return;
                    }
                }
            } catch (error) {
                console.error('Error polling for FNSKUs:', error);
            }

            // Continue polling if under max attempts
            if (attempts < maxAttempts) {
                setTimeout(poll, 5000);
            } else {
                console.log('Max polling attempts reached');
                setPollingForFnskus(false);
            }
        };

        poll();
    };

    const handlePrintLabelClick = (condition, name, fnsku, itemCode, sku, dispositionItem) => {
        // Store the print data and show quantity modal
        setPendingPrintData({ condition, name, fnsku, itemCode, sku, dispositionItem });
        setPrintQty('1');
        setLabelSize('30336'); // Reset to default size
        setShowPrintQtyModal(true);
    };

    const handleConfirmPrintQty = async () => {
        console.log('handleConfirmPrintQty called');
        const qty = parseInt(printQty) || 1;
        if (qty < 1 || qty > 999) {
            toast.error('Please enter a valid quantity between 1 and 999');
            return;
        }

        setShowPrintQtyModal(false);

        if (pendingPrintData) {
            console.log('Pending print data:', pendingPrintData);
            // Fetch the correct FNSKU for this disposition/condition
            let fnskuToUse = pendingPrintData.fnsku; // Default to original FNSKU

            if (pendingPrintData.sku && pendingPrintData.dispositionItem?.amazon_id) {
                console.log('Fetching FNSKU for SKU:', pendingPrintData.sku, 'Condition:', pendingPrintData.dispositionItem.amazon_id);
                try {
                    const response = await ApiClient.post('/api/getfnskubycondition', {
                        sku: pendingPrintData.sku,
                        conditionId: pendingPrintData.dispositionItem.amazon_id
                    });

                    if (response.data) {
                        const result = response.data;
                        console.log('FNSKU API response:', result);
                        if (result.fnsku) {
                            fnskuToUse = result.fnsku;
                            console.log('Using condition-specific FNSKU:', fnskuToUse);
                        } else {
                            console.log('No condition-specific FNSKU found, using default:', fnskuToUse);
                        }
                    } else {
                        console.error('FNSKU API failed with status:', response.status);
                    }
                } catch (error) {
                    console.error('Error fetching condition-specific FNSKU:', error);
                    // Continue with default FNSKU
                }
            } else {
                console.log('Using default FNSKU (no SKU or condition ID):', fnskuToUse);
            }

            console.log('Calling handlePrintLabel with FNSKU:', fnskuToUse);
            await handlePrintLabel(
                pendingPrintData.condition,
                pendingPrintData.name,
                fnskuToUse,
                pendingPrintData.itemCode,
                qty,
                labelSize
            );
        } else {
            console.error('No pending print data!');
        }
    };

    const handlePrintLabel = async (condition, name, fnsku, itemCode, quantity = 1, size = '30336') => {
        console.log('=== DISPOSITION PRINT START ===');
        console.log('Printing label:', condition, name, fnsku, itemCode, 'Quantity:', quantity, 'Size:', size);

        // Validate FNSKU - must have value for barcode
        if (!fnsku || fnsku.trim() === '') {
            toast.error('Cannot print label: FNSKU is missing');
            console.error('FNSKU is empty or null');
            return;
        }

        // Escape XML special characters in text fields
        const safeCondition = escapeXml(condition);
        const safeName = escapeXml(name);
        const safeItemCode = escapeXml(itemCode);

        try {
            // Check if DYMO framework is loaded
            if (!window.dymo || !window.dymo.label || !window.dymo.label.framework) {
                throw new Error('DYMO framework not loaded. Please ensure DYMO Connect software is installed and running.');
            }

            // Get printers
            const printers = window.dymo.label.framework.getPrinters();

            if (!printers || printers.length === 0) {
                throw new Error('No DYMO printers found. Please ensure a DYMO printer is connected and the DYMO Connect software is running.');
            }

            // Find first connected printer (isConnected can be boolean or string)
            const connectedPrinter = printers.find(p => p.isConnected === true || p.isConnected === 'True');
            const printer = connectedPrinter || printers[0];
            const printerName = printer.name;
            console.log('Using printer:', printerName, '(connected:', printer.isConnected, ')');

            // Create label XML based on size selection
            let labelXml;

            if (size === '30252') {
                // 30252 Address Label - 1-1/8" x 3-1/2"
                labelXml = `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips">
    <PaperOrientation>Landscape</PaperOrientation>
    <Id>Address</Id>
    <IsOutlined>false</IsOutlined>
    <PaperName>30252 Address</PaperName>
    <Height>1620</Height>
    <Width>5040</Width>
    <DrawCommands>
        <RoundRectangle X="0" Y="0" Width="1620" Height="5040" Rx="270" Ry="270" />
    </DrawCommands>
    <ObjectInfo>
        <TextObject>
            <Name>ItemCode</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Left</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeItemCode}</String>
                    <Attributes>
                        <Font Family="Arial" Size="12" Bold="True" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="150" Y="50" Width="2300" Height="300" />
    </ObjectInfo>
    <ObjectInfo>
        <TextObject>
            <Name>Condition</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Right</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeCondition}</String>
                    <Attributes>
                        <Font Family="Arial" Size="12" Bold="True" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="2550" Y="50" Width="2400" Height="300" />
    </ObjectInfo>
    <ObjectInfo>
        <TextObject>
            <Name>ProductName</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeName}</String>
                    <Attributes>
                        <Font Family="Arial" Size="8" Bold="False" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="150" Y="380" Width="4800" Height="450" />
    </ObjectInfo>
    <ObjectInfo>
        <BarcodeObject>
            <Name>BarcodeObject</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <Text>${fnsku}</Text>
            <Type>Code128Auto</Type>
            <Size>Medium</Size>
            <TextPosition>Bottom</TextPosition>
            <TextFont Family="Arial" Size="12" Bold="False" Italic="False" Underline="False" Strikeout="False" />
            <CheckSumFont Family="Arial" Size="12" Bold="False" Italic="False" Underline="False" Strikeout="False" />
            <TextEmbedding>None</TextEmbedding>
            <ECLevel>0</ECLevel>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <QuietZonesPadding Left="0" Top="0" Right="0" Bottom="0" />
        </BarcodeObject>
        <Bounds X="50" Y="860" Width="4940" Height="600" />
    </ObjectInfo>
</DieCutLabel>`;
            } else {
                // 30336 Multipurpose Label - 1" x 2-1/8" (default)
                labelXml = `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips">
    <PaperOrientation>Landscape</PaperOrientation>
    <Id>Multipurpose</Id>
    <IsOutlined>false</IsOutlined>
    <PaperName>30336 1 in x 2-1/8 in</PaperName>
    <Height>1440</Height>
    <Width>3060</Width>
    <DrawCommands>
        <RoundRectangle X="0" Y="0" Width="1440" Height="3060" Rx="270" Ry="270" />
    </DrawCommands>
    <ObjectInfo>
        <TextObject>
            <Name>ItemCode</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Left</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeItemCode}</String>
                    <Attributes>
                        <Font Family="Arial" Size="9" Bold="True" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="100" Y="40" Width="1400" Height="220" />
    </ObjectInfo>
    <ObjectInfo>
        <TextObject>
            <Name>Condition</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Right</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeCondition}</String>
                    <Attributes>
                        <Font Family="Arial" Size="9" Bold="True" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="1560" Y="40" Width="1400" Height="220" />
    </ObjectInfo>
    <ObjectInfo>
        <TextObject>
            <Name>ProductName</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <VerticalAlignment>Top</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>${safeName}</String>
                    <Attributes>
                        <Font Family="Arial" Size="6" Bold="False" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="100" Y="280" Width="2860" Height="280" />
    </ObjectInfo>
    <ObjectInfo>
        <BarcodeObject>
            <Name>BarcodeObject</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>True</IsVariable>
            <Text>${fnsku}</Text>
            <Type>Code128Auto</Type>
            <Size>Small</Size>
            <TextPosition>Bottom</TextPosition>
            <TextFont Family="Arial" Size="8" Bold="False" Italic="False" Underline="False" Strikeout="False" />
            <CheckSumFont Family="Arial" Size="8" Bold="False" Italic="False" Underline="False" Strikeout="False" />
            <TextEmbedding>None</TextEmbedding>
            <ECLevel>0</ECLevel>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <QuietZonesPadding Left="0" Top="0" Right="0" Bottom="0" />
        </BarcodeObject>
        <Bounds X="80" Y="580" Width="2900" Height="520" />
    </ObjectInfo>
</DieCutLabel>`;
            }

            // Create label object
            console.log('Label XML:', labelXml.substring(0, 500)); // Log first 500 chars for debugging
            const label = window.dymo.label.framework.openLabelXml(labelXml);
            console.log('Label object created successfully');

            // Print the label multiple times
            for (let i = 0; i < quantity; i++) {
                console.log(`Printing label ${i + 1} of ${quantity}...`);
                label.print(printerName);
                // Small delay between prints to ensure printer processes each job
                if (i < quantity - 1) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            console.log(`${quantity} label(s) sent to printer successfully!`);
            console.log('=== DISPOSITION PRINT END ===');
            toast.success(`${quantity} label(s) sent to printer successfully!`);
        } catch (error) {
            console.error('=== DISPOSITION PRINT ERROR ===');
            console.error('Error printing label:', error);
            console.error('Error message:', error.message);
            console.error('Error stack:', error.stack);
            toast.error(`Failed to print label: ${error.message}\n\nPlease ensure:\n1. DYMO Connect software is installed and running\n2. A DYMO printer is connected\n3. The printer is turned on and ready`);
        }
    };

    const handleSaveDispositions = async () => {
        if (savingRef.current) return; // ignore rapid double-clicks while a save is in flight
        savingRef.current = true;
        try {
            setSaving(true);

            // Prepare dispositions array - include items with quantity >= 0 that have been modified
            const dispositionsToSave = Object.entries(quantities)
                .filter(([_, quantity]) => quantity !== '' && quantity !== undefined)
                .map(([dispositionId, quantity]) => ({
                    dispositionId: parseInt(dispositionId),
                    quantity: parseInt(quantity) || 0,
                    upc: upcs[dispositionId] || null // Include UPC if entered
                }));

            if (dispositionsToSave.length === 0) {
                toast.error('Please enter at least one quantity to save.');
                return;
            }

            // Validate total quantity against expected
            const totalQuantity = dispositionsToSave.reduce((sum, d) => sum + d.quantity, 0);
            if (totalQuantity !== expectedQuantity && expectedQuantity > 0) {
                const confirmed = window.confirm(
                    `Warning: Total quantity entered (${totalQuantity}) does not match shipped quantity (${expectedQuantity}).\n\n` +
                    `Do you want to save anyway?`
                );
                if (!confirmed) {
                    return;
                }
            }

            // Use trackingId prop (from user input), or fall back to row data or trackingData array
            const trackingIdToSave = trackingId ||
                             data?.tracking_id ||
                             (Array.isArray(trackingData) && trackingData.length > 0 ? trackingData[0].tracking_id : null) ||
                             '';

            const response = await ApiClient.post('/api/savedispositions', {
                trackingNumber: trackingIdToSave,
                removalShipment: data?.removal_shipment || '',
                dispositions: dispositionsToSave
            });

            const result = response.data;

            toast.success(`Dispositions saved successfully! Saved ${result.savedCount} items.`);

            // Refresh the parent tracking table (received qty / status) before closing,
            // so the screen reflects the save without a manual page refresh.
            if (onSaved) {
                await onSaved();
            }
            onClose();
        } catch (error) {
            console.error('Error saving dispositions:', error);
            const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to save dispositions. Please try again.';
            toast.error(msg);
        } finally {
            setSaving(false);
            savingRef.current = false;
        }
    };

    // Test print function for debugging
    const handleTestPrint = async () => {
        console.log('=== TEST PRINT START ===');
        try {
            if (!window.dymo || !window.dymo.label || !window.dymo.label.framework) {
                toast.error('DYMO framework not loaded');
                return;
            }

            const printers = window.dymo.label.framework.getPrinters();
            console.log('All printers:', JSON.stringify(printers, null, 2));

            if (!printers || printers.length === 0) {
                toast.error('No DYMO printers found');
                return;
            }

            const connectedPrinter = printers.find(p => p.isConnected === true || p.isConnected === 'True');
            const printer = connectedPrinter || printers[0];
            console.log('Selected printer:', printer.name, 'isConnected:', printer.isConnected);

            // Simple test label
            const testXml = `<?xml version="1.0" encoding="utf-8"?>
<DieCutLabel Version="8.0" Units="twips">
    <PaperOrientation>Landscape</PaperOrientation>
    <Id>Address</Id>
    <IsOutlined>false</IsOutlined>
    <PaperName>30252 Address</PaperName>
    <Height>1620</Height>
    <Width>5040</Width>
    <DrawCommands>
        <RoundRectangle X="0" Y="0" Width="1620" Height="5040" Rx="270" Ry="270" />
    </DrawCommands>
    <ObjectInfo>
        <TextObject>
            <Name>Text</Name>
            <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
            <BackColor Alpha="0" Red="255" Green="255" Blue="255" />
            <LinkedObjectName></LinkedObjectName>
            <Rotation>Rotation0</Rotation>
            <IsMirrored>False</IsMirrored>
            <IsVariable>False</IsVariable>
            <HorizontalAlignment>Center</HorizontalAlignment>
            <VerticalAlignment>Middle</VerticalAlignment>
            <TextFitMode>ShrinkToFit</TextFitMode>
            <UseFullFontHeight>True</UseFullFontHeight>
            <Verticalized>False</Verticalized>
            <StyledText>
                <Element>
                    <String>TEST PRINT</String>
                    <Attributes>
                        <Font Family="Arial" Size="24" Bold="True" Italic="False" Underline="False" Strikeout="False" />
                        <ForeColor Alpha="255" Red="0" Green="0" Blue="0" />
                    </Attributes>
                </Element>
            </StyledText>
        </TextObject>
        <Bounds X="331" Y="150" Width="4230" Height="1200" />
    </ObjectInfo>
</DieCutLabel>`;

            console.log('Test XML:', testXml);

            const label = window.dymo.label.framework.openLabelXml(testXml);
            console.log('Label created:', label);

            label.print(printer.name);
            console.log('Print command sent!');
            toast.success('Test print sent to ' + printer.name);
        } catch (error) {
            console.error('Test print error:', error);
            toast.error('Test print failed: ' + error.message);
        }
        console.log('=== TEST PRINT END ===');
    };

    if (!open || !data) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-75 overflow-auto">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl relative">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
                    aria-label="Close modal"
                >
                    &times;
                </button>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl sm:text-2xl font-bold">{data.item_name}</h2>
                    {process.env.NODE_ENV === 'development' && (
                        <button
                            type="button"
                            onClick={handleTestPrint}
                            className="bg-gray-500 text-white px-3 py-1 text-xs rounded hover:bg-gray-600"
                            title="Test DYMO printer connection"
                        >
                            Test Print
                        </button>
                    )}
                </div>
                {(trackingData?.tracking_id || data?.tracking_id) && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-sm font-semibold text-blue-900">
                            Tracking Number: <span className="font-mono">{trackingData?.tracking_id || data?.tracking_id}</span>
                        </p>
                    </div>
                )}

                {/* LPN Scanner Section */}
                <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded">
                    <div className="flex items-center gap-4">
                        <label htmlFor="lpn-input" className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                            Scan LPN:
                        </label>
                        <input
                            type="text"
                            id="lpn-input"
                            value={lpnInput}
                            onChange={handleLpnInputChange}
                            placeholder="Scan or enter LPN..."
                            className={`flex-1 rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ${
                                lpnError ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300 focus:ring-indigo-500'
                            } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                        />
                    </div>

                    {/* Loading state */}
                    {lpnLoading && (
                        <div className="mt-2 text-sm text-gray-600">
                            🔍 Looking up LPN...
                        </div>
                    )}

                    {/* Error state */}
                    {lpnError && !lpnLoading && lpnInput && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                            <p className="text-sm text-red-700">❌ LPN not found in database</p>
                        </div>
                    )}

                    {/* Success state - Display disposition data */}
                    {lpnData && !lpnLoading && (
                        <div className="mt-3 space-y-2">
                            {/* Red Alert for flagged reasons */}
                            {lpnData.reason && [
                                'UNDELIVERABLE_MISSING_LABEL',
                                'UNDELIVERABLE_UNCLAIMED',
                                'UNDELIVERABLE_CARRIER_MISS_SORTED',
                                'UNDELIVERABLE_FAILED_DELIVERY_ATTEMPTS',
                                'UNDELIVERABLE_INSUFFICIENT_ADDRESS',
                                'UNAUTHORIZED_PURCHASE',
                                'NEVER_ARRIVED',
                                'DAMAGED_BY_CARRIER',
                                'NO_REASON_GIVEN',
                                'DAMAGED_BY_FC',
                                'MISSING_PARTS',
                                'UNDELIVERABLE_REFUSED',
                                'SWITCHEROO',
                                'UNDELIVERABLE_UNKNOWN',
                                'MISSED_ESTIMATED_DELIVERY'
                            ].includes(lpnData.reason) && (
                                <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg">
                                    <h3 className="text-base font-bold text-red-900">⚠️ CREATE CASE REQUIRED</h3>
                                    <p className="mt-1 text-sm text-red-800">
                                        This return reason requires case creation and investigation.
                                    </p>
                                </div>
                            )}

                            {/* Disposition details */}
                            <div className="p-3 bg-white border border-gray-200 rounded">
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    {lpnData.productname && (
                                        <div className="col-span-2">
                                            <span className="font-semibold text-gray-700">Product:</span>
                                            <p className="text-gray-900 font-medium">{lpnData.productname}</p>
                                        </div>
                                    )}
                                    <div>
                                        <span className="font-semibold text-gray-700">Disposition:</span>
                                        <p className="text-gray-900">{lpnData.detaileddisposition || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="font-semibold text-gray-700">Reason:</span>
                                        <p className="text-gray-900">{lpnData.reason || 'N/A'}</p>
                                    </div>
                                    {lpnData.customercomments && (
                                        <div className="col-span-2">
                                            <span className="font-semibold text-gray-700">Customer Comments:</span>
                                            <p className="text-gray-900">{lpnData.customercomments}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="mb-4">
                    <p className="text-sm sm:text-base font-medium">Selected Data:</p>
                    {/* Tab Navigation */}
                    <div className="border-b border-gray-200 mt-2">
                        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                            <button
                                onClick={() => setActiveTab('record')}
                                className={`${
                                    activeTab === 'record'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                } whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium transition-colors`}
                            >
                                Record Disposition
                            </button>
                            <button
                                onClick={() => setActiveTab('case')}
                                className={`${
                                    activeTab === 'case'
                                        ? 'border-indigo-500 text-indigo-600'
                                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                } whitespace-nowrap border-b-2 py-2 px-1 text-sm font-medium transition-colors flex items-center gap-1.5`}
                            >
                                Create Case
                                {generatedCases && generatedCases.length > 0 && (
                                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-blue-600 rounded-full">
                                        {generatedCases.length}
                                    </span>
                                )}
                            </button>
                        </nav>
                    </div>
                </div>
                {/* Record Disposition Tab */}
                {activeTab === 'record' && (
                    <div className="mt-4">
                        {/* Existing Cases Banner */}
                        {generatedCases && generatedCases.length > 0 && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-300 rounded-lg flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-sm font-medium text-blue-800">
                                        {generatedCases.length} existing case{generatedCases.length > 1 ? 's' : ''} found for this item
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('case')}
                                    className="text-sm font-semibold text-blue-700 hover:text-blue-900 underline"
                                >
                                    View Cases
                                </button>
                            </div>
                        )}
                        {/* Create Listings Banner - Show if any disposition with amazon_id is missing FNSKU */}
                        {!loadingFnskus && dispositionData?.some(item =>
                            item.amazon_id !== null &&
                            item.display_order <= 7 &&
                            !fnskus[item.id]
                        ) && (
                            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-yellow-800">
                                        Some conditions are missing FBA listings
                                    </p>
                                    <p className="text-xs text-yellow-600">
                                        Create FBA and MF listings for all conditions to enable label printing
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="bg-blue-600 text-white px-4 py-2 text-sm rounded hover:bg-blue-500 disabled:bg-gray-400"
                                    onClick={handleCheckListings}
                                    disabled={pollingForFnskus}
                                >
                                    {pollingForFnskus ? 'Checking...' : 'Create Listings'}
                                </button>
                            </div>
                        )}
                        {/* Polling indicator */}
                        {pollingForFnskus && (
                            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                <span className="text-sm text-blue-700">Polling for FNSKUs... This may take a minute.</span>
                            </div>
                        )}
                        <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse text-left">
                            <thead className="bg-gray-100">
                            <tr>
                                <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-semibold text-gray-900">ID</th>
                                <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-semibold text-gray-900">Disposition</th>
                                <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-semibold text-gray-900">FNSKU</th>
                                <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-semibold text-gray-900">Quantity</th>
                                <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm text-right">Action</th>
                            </tr>
                            </thead>
                            <tbody>
                            {dispositionData?.length > 0 ? (
                                dispositionData
                                    .filter(item => item.display_order <= 7)
                                    .slice()
                                    .sort((a, b) => a.display_order - b.display_order)
                                    .map((item) => (
                                        <React.Fragment key={item.id}>
                                            <tr className="border-b text-xs sm:text-sm">
                                                <td className="px-2 sm:px-4 py-1">{item.id}</td>
                                                <td className="px-2 sm:px-4 py-1">{item.disposition}</td>
                                                <td className="px-2 sm:px-4 py-1 font-mono text-xs">
                                                    {loadingFnskus ? (
                                                        <div className="flex items-center justify-center">
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                                        </div>
                                                    ) : item.amazon_id === null ? (
                                                        <span className="text-gray-400">-</span>
                                                    ) : fnskus[item.id] ? (
                                                        fnskus[item.id]
                                                    ) : (
                                                        <span className="text-orange-500 text-xs">No Listing</span>
                                                    )}
                                                </td>
                                                <td className="px-2 sm:px-4 py-1">
                                                    <input
                                                        type="number"
                                                        className="w-16 border-gray-300 focus:ring-blue-500 focus:border-blue-500 block text-center text-xs sm:text-sm border rounded-md"
                                                        placeholder="00000"
                                                        value={quantities[item.id] || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '');
                                                            if (value.length <= 5) {
                                                                setQuantities(prev => ({
                                                                    ...prev,
                                                                    [item.id]: value
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td className="px-2 sm:px-4 py-1 text-right">
                                                    {item.amazon_id !== null && fnskus[item.id] && (
                                                        <button
                                                            type="button"
                                                            className="bg-blue-600 text-white px-2 sm:px-3 py-1 text-xs sm:text-sm rounded hover:bg-blue-500"
                                                            onClick={() => handlePrintLabelClick(item.disposition, data.item_name, fnskus[item.id], itemCode, data.sku, item)}
                                                        >
                                                            Print Label
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {/* UPC Input Row - Only show for "Different Unit Received" */}
                                            {dispositionRequiresUpc(item) && (
                                                <tr className="bg-yellow-50 border-b">
                                                    <td colSpan="5" className="px-2 sm:px-4 py-2">
                                                        <div className="flex items-center gap-2">
                                                            <label className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
                                                                Product UPC:
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="flex-1 max-w-xs border-gray-300 focus:ring-yellow-500 focus:border-yellow-500 text-xs sm:text-sm border rounded-md px-2 py-1"
                                                                placeholder="Scan or enter UPC..."
                                                                value={upcs[item.id] || ''}
                                                                onChange={(e) => {
                                                                    setUpcs(prev => ({
                                                                        ...prev,
                                                                        [item.id]: e.target.value
                                                                    }));
                                                                }}
                                                            />
                                                            <span className="text-xs text-gray-500">
                                                                (Required for different unit)
                                                            </span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="px-2 sm:px-4 py-2 text-center text-gray-500 text-xs sm:text-sm">No record dispositions available</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                        </div>
                    </div>
                )}

                {/* Create Case Tab */}
                {activeTab === 'case' && (
                    <div className="mt-4">
                        {/* Previously Generated Cases */}
                        {generatedCases && generatedCases.length > 0 && (
                            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <h3 className="text-sm font-bold text-blue-900 mb-3">Previously Generated Cases</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {generatedCases.map((caseItem, index) => (
                                        <div
                                            key={index}
                                            className="bg-white border border-blue-300 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => {
                                                setQrCodeUrl(caseItem.qrCodeUrl);
                                                setCurrentCaseId(caseItem.caseId);
                                                setCurrentCaseQuantity(caseItem.quantity);
                                                setCurrentCaseDisposition(caseItem.disposition);
                                                setSelectedCaseIndex(index);
                                            }}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-xs font-bold text-indigo-700">Case #{caseItem.caseId}</p>
                                                {selectedCaseIndex === index && (
                                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Active</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-600 truncate" title={caseItem.disposition}>
                                                {caseItem.disposition}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                Qty: {caseItem.quantity}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-1">
                                                {new Date(caseItem.createdAt).toLocaleTimeString()}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-gray-600 mt-3 italic">
                                    Click on a case to view its QR code below
                                </p>
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <table className="min-w-full border-collapse text-left">
                                <thead className="bg-gray-100">
                                <tr>
                                    <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-semibold text-gray-900">ID</th>
                                    <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-semibold text-gray-900">Disposition</th>
                                    <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm font-semibold text-gray-900">Quantity</th>
                                    <th scope="col" className="px-2 sm:px-4 py-1 text-xs sm:text-sm text-right">Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {dispositionData?.length > 0 ? (
                                    dispositionData
                                        .filter(item => item.display_order > 7)
                                        .slice()
                                        .sort((a, b) => a.display_order - b.display_order)
                                        .map((item) => {
                                            const existingCase = getCaseForDisposition(item.id);
                                            const isQrVisible = showQrCodeForDisposition === item.id;

                                            return (
                                                <tr key={item.id} className="border-b text-xs sm:text-sm">
                                                    <td className="px-2 sm:px-4 py-1">{item.id}</td>
                                                    <td className="px-2 sm:px-4 py-1">
                                                        {item.disposition}
                                                        {existingCase && (
                                                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                                Case #{existingCase.caseId}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 sm:px-4 py-1">
                                                        <input
                                                            type="number"
                                                            className="w-16 border-gray-300 focus:ring-blue-500 focus:border-blue-500 block text-center text-xs sm:text-sm border rounded-md"
                                                            placeholder="00000"
                                                            value={quantities[item.id] || ''}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(/\D/g, '');
                                                                if (value.length <= 5) {
                                                                    setQuantities(prev => ({
                                                                        ...prev,
                                                                        [item.id]: value
                                                                    }));
                                                                }
                                                            }}
                                                            disabled={!!existingCase}
                                                        />
                                                    </td>
                                                    <td className="px-2 sm:px-4 py-1 text-right">
                                                        {existingCase ? (
                                                            <div className="flex flex-col gap-1">
                                                                <button
                                                                    type="button"
                                                                    className={`${
                                                                        isQrVisible
                                                                            ? 'bg-orange-600 hover:bg-orange-500'
                                                                            : 'bg-blue-600 hover:bg-blue-500'
                                                                    } text-white px-2 sm:px-3 py-1 text-xs sm:text-sm rounded transition-colors`}
                                                                    onClick={() => handleToggleQrCode(item.id)}
                                                                >
                                                                    {isQrVisible ? 'Hide QR Code' : 'Add Photos to Case'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="bg-purple-600 text-white px-2 sm:px-3 py-1 text-xs sm:text-sm rounded hover:bg-purple-500 transition-colors"
                                                                    onClick={() => handleSendEmailClick(existingCase)}
                                                                >
                                                                    Send link by email
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="bg-green-600 text-white px-2 sm:px-3 py-1 text-xs sm:text-sm rounded hover:bg-green-500"
                                                                onClick={() => handleGenerateQrCode(item)}
                                                            >
                                                                Upload Photos
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="px-2 sm:px-4 py-2 text-center text-gray-500 text-xs sm:text-sm">No case dispositions available</td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>

                        {/* QR Code Display - Only show in Create Case tab */}
                        {qrCodeUrl && currentCaseId && (
                            <div className="mt-6 p-4 bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-lg">
                                <div className="text-center mb-4">
                                    <p className="text-sm font-semibold text-gray-700">Active Case ID:</p>
                                    <p className="text-2xl font-bold text-indigo-600 mb-1">{currentCaseId}</p>
                                    <p className="text-xs text-gray-600 mb-3">{currentCaseDisposition} - Qty: {currentCaseQuantity}</p>
                                    <button
                                        type="button"
                                        onClick={() => handlePrintCaseLabel(currentCaseId, currentCaseQuantity, currentCaseDisposition)}
                                        className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-500"
                                    >
                                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                        </svg>
                                        Reprint Case Label
                                    </button>
                                </div>
                                <div className="text-center">
                                    <p className="text-sm font-semibold mb-2">Scan QR Code to Upload Photos:</p>
                                    <div className="inline-block p-4 bg-white border-2 border-indigo-300 rounded-lg shadow-lg">
                                        <QRCodeCanvas value={qrCodeUrl} size={156} />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Quantity Summary */}
                {expectedQuantity > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 border rounded-lg">
                        <div className="flex justify-between items-center text-sm">
                            <span className="font-semibold text-gray-700">Quantity Summary:</span>
                            <div className="flex items-center gap-3">
                                <span className="text-gray-600">
                                    Entered: <span className="font-semibold">{totalEntered}</span>
                                </span>
                                <span className="text-gray-600">
                                    Expected: <span className="font-semibold">{expectedQuantity}</span>
                                </span>
                                {totalEntered === expectedQuantity ? (
                                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">✓ Match</span>
                                ) : totalEntered > expectedQuantity ? (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">⚠ Over</span>
                                ) : totalEntered < expectedQuantity ? (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">⚠ Under</span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}

                {/* Save All Button */}
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={handleSaveDispositions}
                        disabled={saving}
                        className="bg-green-600 text-white px-4 sm:px-6 py-2 text-sm sm:text-base rounded hover:bg-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Saving...' : 'Save All Dispositions'}
                    </button>
                </div>
            </div>

            {/* Print Quantity Modal */}
            {showPrintQtyModal && (
                <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-96">
                        <h3 className="text-lg font-semibold mb-4">Print Labels</h3>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Label Size
                            </label>
                            <select
                                value={labelSize}
                                onChange={(e) => setLabelSize(e.target.value)}
                                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 py-2 px-3"
                            >
                                <option value="30336">30336 - 1" x 2-1/8"</option>
                                <option value="30252">30252 - 1-1/8" x 3-1/2"</option>
                            </select>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Quantity
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="999"
                                value={printQty}
                                onChange={(e) => setPrintQty(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleConfirmPrintQty();
                                    }
                                }}
                                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-center text-lg font-semibold py-2"
                                autoFocus
                            />
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setShowPrintQtyModal(false)}
                                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmPrintQty}
                                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
                            >
                                Print
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showEmailModal && (
                <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-96">
                        <h3 className="text-lg font-semibold mb-4">Send QR Code Link by Email</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Enter the email address to send the QR code link for case {selectedCaseForEmail?.caseId}
                        </p>
                        <input
                            type="email"
                            value={emailInput}
                            onChange={(e) => setEmailInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !emailSending) {
                                    handleSendEmail();
                                }
                            }}
                            placeholder="Enter email address"
                            className="w-full border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 px-3 py-2 mb-4"
                            autoFocus
                            disabled={emailSending}
                        />
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowEmailModal(false);
                                    setEmailInput('');
                                    setSelectedCaseForEmail(null);
                                }}
                                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                                disabled={emailSending}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSendEmail}
                                className="flex-1 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                disabled={emailSending || !emailInput}
                            >
                                {emailSending ? 'Sending...' : 'Send Email'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Listings Confirmation Modal */}
            {showListingModal && existingListings && (
                <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black bg-opacity-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-[500px] max-h-[80vh] overflow-auto">
                        <h3 className="text-lg font-semibold mb-4">Create Listings for {data?.asin1}</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Review what will be created. Existing listings will be skipped.
                        </p>

                        <table className="w-full text-sm mb-4">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="px-3 py-2 text-left">Condition</th>
                                    <th className="px-3 py-2 text-center">FBA</th>
                                    <th className="px-3 py-2 text-center">MF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {existingListings.existing?.map(item => (
                                    <tr key={item.conditionId} className="border-b">
                                        <td className="px-3 py-2">{item.condition}</td>
                                        <td className="px-3 py-2 text-center">
                                            {item.fba ? (
                                                <span className="text-green-600 font-medium">✓ Exists</span>
                                            ) : (
                                                <span className="text-blue-600">Will Create</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            {item.mf ? (
                                                <span className="text-green-600 font-medium">✓ Exists</span>
                                            ) : (
                                                <span className="text-blue-600">Will Create</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="text-xs text-gray-500 mb-4">
                            <p>• FBA listings will get FNSKUs assigned by Amazon (takes 1-2 minutes)</p>
                            <p>• MF listings are for merchant fulfilled sales from your warehouse</p>
                        </div>

                        <div className="flex gap-2 justify-end">
                            <button
                                type="button"
                                onClick={() => setShowListingModal(false)}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                                disabled={creatingListings}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleCreateListings}
                                disabled={creatingListings}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 disabled:bg-gray-400"
                            >
                                {creatingListings ? 'Creating...' : 'Create Missing Listings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReceiveUnitsModal;
